/**
 * SSE consumption for the sentinel-design routes, following the same
 * line-buffered `fetch` + `data: ` reader the Experiment Lab already uses
 * (`useExperimentApi.train`) rather than introducing a second pattern.
 *
 * Two envelope spellings are accepted on purpose. The design routes are
 * specified as `{type: "progress"}` / `{type: "result", result}` /
 * `{type: "error", error}`, while the experiment route that established the
 * pattern actually emits `{event: "result", data}`. Reading both means the
 * frontend does not break whichever spelling fim-service ships.
 */

export type DesignFrameKind = 'progress' | 'result' | 'error'

export interface DesignFrame<TResult> {
  kind: DesignFrameKind
  /** Present for `progress`; the raw frame, so callers can read stage fields. */
  progress?: Record<string, unknown>
  /** Present for `result`. */
  result?: TResult
  /** Present for `error`. */
  error?: string
}

const asRecord = (value: unknown): Record<string, unknown> | null =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null

/**
 * Split a decoded buffer into whole SSE frames, returning the trailing
 * fragment that has not been terminated by a blank line yet.
 */
export const splitSseFrames = (
  buffer: string
): { frames: string[]; rest: string } => {
  const parts = buffer.split('\n\n')
  return { frames: parts.slice(0, -1), rest: parts[parts.length - 1] ?? '' }
}

/** The `data:` payload of one SSE frame; multi-line data is newline-joined. */
export const sseFrameData = (frame: string): string | null => {
  const lines = frame
    .split('\n')
    .filter((line) => line.startsWith('data:'))
    .map((line) => line.slice(5).replace(/^ /, ''))
  return lines.length ? lines.join('\n') : null
}

/** Classify one already-parsed frame body into progress / result / error. */
export const classifyDesignFrame = <TResult>(
  body: unknown
): DesignFrame<TResult> | null => {
  const record = asRecord(body)
  if (!record) return null

  const type = record.type ?? record.event
  const payload = 'result' in record ? record.result : record.data

  if (type === 'error') {
    const message = record.error ?? record.message
    return {
      kind: 'error',
      error: typeof message === 'string' ? message : 'stream failed',
    }
  }
  // A frame carrying a payload is a result even when it forgot to say so.
  if (type === 'result' || (type === undefined && payload !== undefined)) {
    return { kind: 'result', result: payload as TResult }
  }
  return { kind: 'progress', progress: record }
}

/** Parse a raw SSE frame (headers included) into a classified frame. */
export const parseDesignFrame = <TResult>(
  frame: string
): DesignFrame<TResult> | null => {
  const data = sseFrameData(frame)
  if (data === null || data === '' || data === '[DONE]') return null
  try {
    return classifyDesignFrame<TResult>(JSON.parse(data))
  } catch {
    // A frame that is not JSON is not actionable; keep reading the stream.
    return null
  }
}

/**
 * The body stopped arriving before the stream ended: the server, a proxy or
 * the network closed the connection. Browsers report this with the same bare
 * `TypeError` as a request that never connected, so the read loop names it.
 * Once the stream has opened the server already holds the message, and a
 * caller can tell the user the turn is resumable instead of "Failed to fetch".
 */
export class StreamDroppedError extends Error {
  constructor(cause: Error) {
    super(cause.message)
    this.name = 'StreamDroppedError'
  }
}

export interface ConsumeDesignStreamOptions<TResult> {
  onProgress?: (progress: Record<string, unknown>) => void
  onResult?: (result: TResult) => void
}

/**
 * Drain an SSE response body, forwarding progress frames and returning the
 * last result frame. Throws when the stream reports an error frame, so callers
 * handle transport and in-band failures in one `catch`.
 */
export const consumeDesignStream = async <TResult>(
  body: ReadableStream<Uint8Array> | null,
  { onProgress, onResult }: ConsumeDesignStreamOptions<TResult> = {}
): Promise<TResult | null> => {
  if (!body) throw new Error('No response body for the design stream')
  const reader = body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let result: TResult | null = null
  let streamError: string | null = null

  for (;;) {
    let chunk: ReadableStreamReadResult<Uint8Array>
    try {
      chunk = await reader.read()
    } catch (err) {
      if (err instanceof TypeError) throw new StreamDroppedError(err)
      throw err
    }
    const { done, value } = chunk
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    const { frames, rest } = splitSseFrames(buffer)
    buffer = rest
    for (const raw of frames) {
      const frame = parseDesignFrame<TResult>(raw)
      if (!frame) continue
      if (frame.kind === 'error') {
        streamError = frame.error ?? 'stream failed'
      } else if (frame.kind === 'result') {
        result = frame.result ?? null
        if (result !== null) onResult?.(result)
      } else if (frame.progress) {
        onProgress?.(frame.progress)
      }
    }
  }

  if (streamError) throw new Error(streamError)
  return result
}

/** Human-readable one-liner for a progress frame, for the progress log. */
export const describeDesignProgress = (
  progress: Record<string, unknown>
): string => {
  const parts: string[] = []
  for (const key of ['stage', 'method', 'message']) {
    const value = progress[key]
    if (typeof value === 'string' && value) parts.push(value)
  }
  const step = progress.step
  const steps = progress.steps
  if (typeof step === 'number') {
    parts.push(typeof steps === 'number' ? `${step}/${steps}` : `step ${step}`)
  }
  return parts.length ? parts.join(' · ') : JSON.stringify(progress)
}
